/**
 * OnboardingAllergies.jsx — v213.7 (C-A-OB-FIX)
 *
 * Screen 5 of 7. AI-prefilled cards if Auto-fill ran; type-to-search
 * picker for manual additions.
 *
 * v213.7 changes:
 *  - Manual add uses a type-to-search picker (MEDICATION_ALLERGIES /
 *    FOOD_ALLERGIES / CONTACT_ALLERGIES from medicalLists.js — same
 *    source the Profile editor uses).
 *  - "No known allergies" upgraded from text link to SecondaryButton.
 */
import { useState, useMemo } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { ShieldAlert, Search, X, Check } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useOnboardingStore } from '../../stores'
import {
  MEDICATION_ALLERGIES,
  FOOD_ALLERGIES,
  CONTACT_ALLERGIES,
} from '../../data/medicalLists'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  OrellaBubble,
  FilledBanner,
  PrimaryButton,
  SecondaryButton,
  Footer,
  ScrollBody,
  ConfirmCard,
  AddAnotherButton,
  EmptyState,
  TextField,
  SegmentedSelect,
  useScrollToTop,
  NAVY,
  BLUE,
  ICE_BLUE,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
  LIGHT_BLUE,
  PROFILE_STEPS,
} from './onboardingPrimitives'

const SEVERITY_BG = { Severe: '#FEE2E2', Moderate: '#FEF3C7', Mild: ICE_BLUE, Unknown: '#F2F4F7' }
const SEVERITY_TEXT = { Severe: '#991B1B', Moderate: '#92400E', Mild: NAVY, Unknown: TEXT_MUTED }

/**
 * AddAllergySheet — type-to-search across MED/FOOD/CONTACT allergy lists.
 * Step 1: pick type (med / food / contact)
 * Step 2: search the matching list
 * Step 3: optional severity + reaction details
 */
function AddAllergySheet({ onSave, onClose }) {
  const { t } = useI18n()
  const [step, setStep] = useState('type') // 'type' | 'search' | 'details'
  const [type, setType] = useState('medication')
  const [pick, setPick] = useState(null) // { id, name, category, custom }
  const [severity, setSeverity] = useState('Mild')
  const [reaction, setReaction] = useState('')
  const [query, setQuery] = useState('')

  const list = type === 'medication' ? MEDICATION_ALLERGIES
    : type === 'food' ? FOOD_ALLERGIES
    : CONTACT_ALLERGIES

  const results = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return list.filter((x) => x.common).slice(0, 30)
    return list.filter((x) => x.name.toLowerCase().includes(term)).slice(0, 40)
  }, [query, list])

  const handleSelect = (item) => {
    setPick(item)
    setStep('details')
  }

  const handleAddCustom = () => {
    if (!query.trim()) return
    setPick({
      id: `custom-${Date.now()}`,
      name: query.trim(),
      custom: true,
    })
    setStep('details')
  }

  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(21,53,79,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full bg-white"
        style={{
          maxWidth: 460, maxHeight: '88vh',
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          padding: '20px 18px 22px',
          paddingBottom: 'max(22px, env(safe-area-inset-bottom, 0px))',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 500, color: NAVY }}>
            {step === 'type'    && t('onboarding.allergies.addTitle', 'Add an allergy')}
            {step === 'search'  && t('onboarding.allergies.search', 'Search allergies')}
            {step === 'details' && (pick?.name || '')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center"
            style={{ width: 32, height: 32, background: '#F2F4F7', border: 'none', cursor: 'pointer' }}
            aria-label="Close"
          >
            <X size={16} color={NAVY} />
          </button>
        </div>

        {/* Step 1: type */}
        {step === 'type' && (
          <>
            <p style={{ fontSize: 14, color: TEXT_MUTED, marginBottom: 14 }}>
              {t('onboarding.allergies.typeQuestion', 'What kind of allergy?')}
            </p>
            <div className="flex flex-col gap-2">
              {[
                { v: 'medication', l: t('onboarding.allergies.medication', 'Drug or medication') },
                { v: 'food',       l: t('onboarding.allergies.food', 'Food') },
                { v: 'contact',    l: t('onboarding.allergies.contact', 'Environmental or contact (latex, pollen, etc.)') },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => { setType(opt.v); setStep('search'); setQuery('') }}
                  className="w-full text-left transition"
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    background: '#fff',
                    border: `1.5px solid ${BORDER_LIGHT}`,
                    color: NAVY,
                    fontSize: 14.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 2: search */}
        {step === 'search' && (
          <>
            <div className="flex items-center gap-2" style={{
              padding: '10px 12px', borderRadius: 11,
              border: `1.5px solid ${BORDER_LIGHT}`, marginBottom: 12,
            }}>
              <Search size={18} color={TEXT_HINT} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('onboarding.allergies.searchPh', 'Type to search...')}
                className="flex-1 focus:outline-none"
                style={{ fontSize: 15, color: NAVY, border: 'none', background: 'transparent' }}
              />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {results.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className="w-full text-left transition"
                  style={{
                    padding: '12px 12px',
                    fontSize: 14.5,
                    color: NAVY,
                    background: '#fff',
                    border: 'none',
                    borderBottom: `1px solid ${BORDER_LIGHT}`,
                    cursor: 'pointer',
                  }}
                >
                  {item.name}
                  {item.category && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: TEXT_HINT }}>{item.category}</span>
                  )}
                </button>
              ))}
              {results.length === 0 && query.trim() && (
                <button
                  onClick={handleAddCustom}
                  className="w-full text-left transition"
                  style={{
                    padding: '14px 12px',
                    fontSize: 14.5,
                    color: BLUE,
                    fontWeight: 500,
                    background: ICE_BLUE,
                    border: `1.5px dashed ${LIGHT_BLUE}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    marginTop: 4,
                  }}
                >
                  + {t('onboarding.allergies.addCustom', { defaultValue: `Add "${query.trim()}"`, name: query.trim() })}
                </button>
              )}
            </div>
            <button
              onClick={() => setStep('type')}
              style={{
                marginTop: 14, padding: '8px 0', background: 'transparent',
                border: 'none', color: TEXT_HINT, fontSize: 13, cursor: 'pointer',
              }}
            >
              ← {t('onboarding.allergies.changeType', 'Change category')}
            </button>
          </>
        )}

        {/* Step 3: severity + reaction */}
        {step === 'details' && pick && (
          <>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 14 }}>
              {t('onboarding.allergies.detailsHint', 'A bit more detail (optional but helpful for doctors).')}
            </p>
            <SegmentedSelect
              label={t('onboarding.allergies.severity', 'Severity')}
              value={severity}
              onChange={setSeverity}
              required={false}
              options={[
                { value: 'Mild',     label: t('onboarding.allergies.mild', 'Mild') },
                { value: 'Moderate', label: t('onboarding.allergies.moderate', 'Moderate') },
                { value: 'Severe',   label: t('onboarding.allergies.severe', 'Severe') },
              ]}
            />
            <TextField
              label={t('onboarding.allergies.reaction', 'Reaction (optional)')}
              value={reaction}
              onChange={setReaction}
              placeholder={t('onboarding.allergies.reactionPh', 'e.g. hives, swelling, trouble breathing')}
              required={false}
            />
            <div style={{ marginTop: 8 }}>
              <PrimaryButton
                data-bar-exempt="saves the item inside its add sheet; the step's own Continue is in the Footer bar"
                onClick={() => {
                  onSave({
                    id: pick.id,
                    name: pick.name,
                    type,
                    severity,
                    reaction,
                    custom: !!pick.custom,
                    source: 'manual',
                    confirmed: true,
                  })
                  onClose()
                }}
              >
                {t('common.add', 'Add allergy')}
              </PrimaryButton>
              <SecondaryButton onClick={() => setStep('search')}>
                {t('onboarding.allergies.back', '← Back to search')}
              </SecondaryButton>
            </div>
          </>
        )}
      </div>
    </div>
    </OverlayPortal>
  )
}

export default function OnboardingAllergies({ onNext, onBack }) {
  const { t } = useI18n()
  const allergies = useOnboardingStore((s) => s.allergies)
  const setAllergies = useOnboardingStore((s) => s.setAllergies)
  const autofillResult = useOnboardingStore((s) => s.autofillResult)

  const [sheetOpen, setSheetOpen] = useState(false)

  useScrollToTop('allergies')

  const items = allergies.items
  const hasAutofill = !!autofillResult

  const toggleConfirm = (id) => {
    setAllergies({
      items: items.map((it) => (it.id === id ? { ...it, confirmed: !it.confirmed } : it)),
      noKnown: false,
    })
  }
  // review r1 (M2): only this per-item removal is recorded; "No known allergies" below clears
  // the list without recording anything. PROD-473: the store keeps it as substance + reaction
  // (useOnboardingStore allergyStillRemoved).
  const removeItem = (id) => {
    setAllergies({ items: items.filter((it) => it.id !== id), noKnown: allergies.noKnown }, { perItem: true })
  }
  const addItem = (item) => {
    setAllergies({ items: [...items, item], noKnown: false })
  }
  const declareNoKnown = () => {
    setAllergies({ items: [], noKnown: true })
    onNext()
  }

  const canContinue = items.length > 0 || allergies.noKnown

  return (
    <>
      <OnboardingShell>
        <ScreenHeader onBack={onBack} step={2} total={PROFILE_STEPS} />
        <ScreenIntro
          eyebrow={t('onboarding.allergies.eyebrow', 'Allergies')}
          title={hasAutofill
            ? t('onboarding.allergies.titleAi', 'Confirm your allergies')
            : t('onboarding.allergies.titleManual', 'Any allergies?')}
          dialogue={hasAutofill
            ? t('onboarding.allergies.dialogueAi', "I filled these in from your upload. Tap to confirm, edit the details, or remove anything that's not right.")
            : t('onboarding.allergies.dialogueManual', 'List anything you react to — medications, foods, latex. This keeps your prescriptions safe.')}
        />

        <ScrollBody>
          {hasAutofill && items.length > 0 && (
            <FilledBanner>{t('onboarding.allergies.filled', 'Filled by Orella from your record — review & confirm')}</FilledBanner>
          )}
          {items.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title={t('onboarding.allergies.empty', 'No allergies added yet')}
              ctaLabel={t('onboarding.allergies.addCta', 'Add allergy')}
              onAdd={() => setSheetOpen(true)}
            />
          ) : (
            <>
              {items.map((item) => (
                <ConfirmCard
                  key={item.id}
                  confirmed={item.confirmed}
                  onToggle={() => toggleConfirm(item.id)}
                  onRemove={() => removeItem(item.id)}
                >
                  <div className="flex items-center gap-1.5" style={{ marginBottom: 5 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 500, color: NAVY }}>{item.name}</span>
                    {item.source === 'AI' && (
                      <span style={{ fontSize: 10.5, fontWeight: 500, padding: '2px 6px', borderRadius: 4, background: ICE_BLUE, color: BLUE }}>AI</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span style={{
                      fontSize: 11.5, fontWeight: 500, padding: '2px 8px', borderRadius: 999,
                      background: SEVERITY_BG[item.severity] || SEVERITY_BG.Unknown,
                      color: SEVERITY_TEXT[item.severity] || SEVERITY_TEXT.Unknown,
                    }}>
                      {item.severity}
                    </span>
                    {item.reaction && <span style={{ fontSize: 13, color: TEXT_MUTED }}>{item.reaction}</span>}
                  </div>
                </ConfirmCard>
              ))}
              <AddAnotherButton
                label={t('common.addAnother', 'Add another')}
                onClick={() => setSheetOpen(true)}
              />
            </>
          )}
        </ScrollBody>

        <Footer>
          <PrimaryButton onClick={onNext} disabled={!canContinue}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
          <SecondaryButton onClick={declareNoKnown}>
            {t('onboarding.allergies.noKnown', 'No known allergies')}
          </SecondaryButton>
        </Footer>
      </OnboardingShell>

      {sheetOpen && <AddAllergySheet onSave={addItem} onClose={() => setSheetOpen(false)} />}
    </>
  )
}
